import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  BookOpen,
  Building2,
  Check,
  Compass,
  Flag,
  Lightbulb,
  Mail,
  Package,
  Plus,
  Sparkles,
} from 'lucide-react'
import {
  useAddCompanyMilestone,
  useAddCompanyTodo,
  useCompanyBrainDocs,
  useCompanyMilestones,
  useCompanyPendingSuggestions,
  useCompanyPulse,
  useToggleCompanyTodo,
  type CompanyBrainDoc_,
  type CompanyMilestoneWithTodos,
} from 'src/api/db/apiCompany'
import { PageContainer } from 'src/shared/components/PageContainer'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const SLUG_ORDER = [
  'who',
  'problems',
  'product',
  'how_we_work',
  'how_we_test',
  'gtm',
  'roadmap',
  'insights',
] as const

const SLUG_TONE: Record<string, string> = {
  who: 'from-sky-50 via-white to-blue-50/40',
  problems: 'from-orange-50 via-white to-amber-50/40',
  product: 'from-violet-50 via-white to-fuchsia-50/40',
  how_we_work: 'from-teal-50 via-white to-emerald-50/40',
  how_we_test: 'from-amber-50 via-white to-yellow-50/40',
  gtm: 'from-rose-50 via-white to-orange-50/30',
  roadmap: 'from-indigo-50 via-white to-sky-50/40',
  insights: 'from-lime-50 via-white to-emerald-50/30',
}

const firstPlainLine = (body: string) => {
  const line = body
    .split('\n')
    .map((row) => row.replace(/^#+\s*/, '').replace(/\*+/g, '').trim())
    .find((row) => row.length > 40)
  return line ?? body.slice(0, 180)
}

const formatDay = (iso: string, locale: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const PulseCard = ({
  label,
  value,
  hint,
  icon: Icon,
  tone,
}: {
  label: string
  value: number
  hint: string
  icon: typeof Package
  tone: { wash: string; icon: string }
}) => (
  <Column className="card-new relative min-w-0 gap-3 overflow-hidden p-4">
    <div
      className={cn(
        'pointer-events-none absolute inset-0 bg-gradient-to-br via-transparent to-transparent',
        tone.wash
      )}
    />
    <Row className="relative items-center gap-2">
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          tone.icon
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <p className="text-sm font-medium">{label}</p>
    </Row>
    <p className="relative text-3xl font-semibold tabular-nums">{value}</p>
    <p className="relative text-xs text-muted-foreground">{hint}</p>
  </Column>
)

const MilestoneCard = ({
  milestone,
  locale,
}: {
  milestone: CompanyMilestoneWithTodos
  locale: string
}) => {
  const { t } = useLocalization()
  const toggleTodo = useToggleCompanyTodo()
  const addTodo = useAddCompanyTodo()
  const [draft, setDraft] = useState('')
  const todos = milestone.company_todo
  const doneCount = todos.filter((todo) => todo.done).length
  const progress = todos.length ? doneCount / todos.length : 0
  const status = milestone.status

  const statusLabel =
    status === 'done'
      ? t('Done', 'Klart')
      : status === 'now'
        ? t('Now', 'Nu')
        : t('Next', 'Nästa')

  return (
    <Column className="relative gap-4 pl-8">
      <div
        className={cn(
          'absolute left-0 top-2 h-3.5 w-3.5 rounded-full border-2 bg-background',
          status === 'now' && 'border-emerald-500 bg-emerald-400 shadow-chip',
          status === 'done' && 'border-stone-400 bg-stone-300',
          status === 'upcoming' && 'border-sky-400 bg-sky-200'
        )}
      />
      <div className="absolute bottom-0 left-[6px] top-6 w-px bg-gradient-to-b from-stone-200 to-transparent" />
      <Column
        className={cn(
          'card-new gap-4 overflow-hidden p-5',
          status === 'now' &&
            'border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/40'
        )}
      >
        <Row className="items-start justify-between gap-3">
          <Column className="min-w-0 gap-1">
            <Row className="items-center gap-2">
              <Badge
                variant={
                  status === 'now'
                    ? 'green'
                    : status === 'done'
                      ? 'gray'
                      : 'blue'
                }
                size="sm"
              >
                {statusLabel}
              </Badge>
              <p className="text-xs tabular-nums text-muted-foreground">
                {formatDay(milestone.target_date, locale)}
              </p>
            </Row>
            <p className="text-base font-medium">{milestone.title}</p>
            {milestone.summary ? (
              <p className="text-sm text-muted-foreground">
                {milestone.summary}
              </p>
            ) : null}
          </Column>
          <p className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {doneCount}/{todos.length || 0}
          </p>
        </Row>
        <div className="h-1.5 overflow-hidden rounded-full bg-stone-100">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-300',
              status === 'now'
                ? 'bg-gradient-to-r from-emerald-400 to-teal-600'
                : 'bg-gradient-to-r from-sky-400 to-indigo-500'
            )}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <Column className="gap-1.5">
          {todos.map((todo) => (
            <button
              key={todo.id}
              type="button"
              onClick={() =>
                toggleTodo.mutate({ id: todo.id, done: !todo.done })
              }
              className="group flex items-start gap-3 rounded-xl px-1 py-1.5 text-left hover:bg-stone-50"
            >
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                  todo.done
                    ? 'border-emerald-500 bg-gradient-to-br from-emerald-400 to-teal-600 text-white'
                    : 'border-stone-300 bg-white group-hover:border-stone-400'
                )}
              >
                {todo.done ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
              </span>
              <span
                className={cn(
                  'text-sm',
                  todo.done && 'text-muted-foreground line-through'
                )}
              >
                {todo.title}
              </span>
            </button>
          ))}
        </Column>
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            const title = draft.trim()
            if (!title) return
            addTodo.mutate(
              { milestoneId: milestone.id, title },
              { onSuccess: () => setDraft('') }
            )
          }}
        >
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('Add a todo', 'Lägg till en todo')}
            className="h-9"
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={!draft.trim() || addTodo.isPending}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </form>
      </Column>
    </Column>
  )
}

const StrategyCard = ({
  doc,
  selected,
  onSelect,
}: {
  doc: CompanyBrainDoc_
  selected: boolean
  onSelect: () => void
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      'card-new min-w-0 overflow-hidden p-4 text-left transition-transform duration-200 hover:-translate-y-0.5',
      selected && 'ring-1 ring-stone-900/10',
      `bg-gradient-to-br ${SLUG_TONE[doc.slug] ?? 'from-stone-50 to-white'}`
    )}
  >
    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {doc.slug.replace(/_/g, ' ')}
    </p>
    <p className="mt-1 text-sm font-medium">{doc.title}</p>
    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
      {firstPlainLine(doc.body)}
    </p>
  </button>
)

export const AdminCompanyPage = () => {
  const { t, locale } = useLocalization()
  const dateLocale = locale === 'sv' ? 'sv-SE' : 'en-GB'
  const { data: docs, isPending: loadingDocs } = useCompanyBrainDocs()
  const { data: pulse, isPending: loadingPulse } = useCompanyPulse()
  const { data: milestones, isPending: loadingMilestones } =
    useCompanyMilestones()
  const { data: suggestions } = useCompanyPendingSuggestions()
  const addMilestone = useAddCompanyMilestone()
  const [selectedSlug, setSelectedSlug] = useState<string>('who')
  const [newTitle, setNewTitle] = useState('')
  const [newDate, setNewDate] = useState('')

  const historyDoc = (docs ?? []).find((doc) => doc.slug === 'history')
  const orderedDocs = [...(docs ?? [])]
    .filter((doc) => doc.slug !== 'history')
    .sort(
      (a, b) =>
        SLUG_ORDER.indexOf(a.slug as (typeof SLUG_ORDER)[number]) -
        SLUG_ORDER.indexOf(b.slug as (typeof SLUG_ORDER)[number])
    )
  const selected =
    orderedDocs.find((doc) => doc.slug === selectedSlug) ?? orderedDocs[0]
  const who = orderedDocs.find((doc) => doc.slug === 'who')
  const isLoading = loadingDocs || loadingPulse || loadingMilestones

  if (isLoading) return <FullScreenLoader />

  return (
    <PageContainer width="xl" className="gap-10 pb-16">
      <Column className="relative gap-4 overflow-hidden rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-gradient-to-br from-stone-900 via-stone-800 to-emerald-950 px-7 py-8 text-white shadow-soft">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-400/20 blur-3xl" />
        <Row className="items-center gap-2 text-emerald-200/90">
          <Compass className="h-4 w-4" strokeWidth={2.2} />
          <p className="text-xs font-medium uppercase tracking-[0.18em]">
            {t('Company brain', 'Bolagshjärnan')}
          </p>
        </Row>
        <h1 className="max-w-3xl text-white">
          {t(
            'Build the best merch company, in the shortest honest time.',
            'Bygg det bästa merch-bolaget, på kortast ärliga tid.'
          )}
        </h1>
        <p className="max-w-2xl text-sm text-stone-200/90">
          {who
            ? firstPlainLine(who.body)
            : t(
                'Who we build for, what we are building, and how we test it — now and later.',
                'Vem vi bygger för, vad vi bygger, och hur vi testar — nu och sen.'
              )}
        </p>
      </Column>

      {suggestions && suggestions.length > 0 ? (
        <Column className="gap-3 rounded-[var(--radius-outer)] border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50/50 p-5">
          <Row className="items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-700" />
            <p className="text-sm font-medium">
              {t(
                `${suggestions.length} strategy suggestions waiting in Slack`,
                `${suggestions.length} strategiförslag väntar i Slack`
              )}
            </p>
          </Row>
          <Column className="gap-2">
            {suggestions.slice(0, 4).map((row) => (
              <p key={row.id} className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {row.target_slug}
                </span>
                {' — '}
                {row.title}
              </p>
            ))}
          </Column>
          <p className="text-xs text-muted-foreground">
            {t(
              'Reply accept or reject in #company-brain. Nothing writes itself.',
              'Svara accept eller reject i #company-brain. Inget skriver sig självt.'
            )}
          </p>
        </Column>
      ) : null}

      <Column className="gap-3">
        <Column className="gap-0.5">
          <p className="text-sm font-medium">{t('Pulse', 'Puls')}</p>
          <p className="text-sm text-muted-foreground">
            {t(
              'Live numbers from the product. The same pulse posts to Slack #daily-updates each morning.',
              'Live-siffror från produkten. Samma puls går till Slack #daily-updates varje morgon.'
            )}
          </p>
        </Column>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
          <PulseCard
            label={t('Active requests', 'Aktiva förfrågningar')}
            value={pulse?.activeRequests ?? 0}
            hint={t('In the pipeline right now', 'I pipen just nu')}
            icon={Package}
            tone={{
              wash: 'from-sky-400/16',
              icon: 'bg-sky-100 text-sky-700',
            }}
          />
          <PulseCard
            label={t('Delivered', 'Levererade')}
            value={pulse?.delivered ?? 0}
            hint={t('Orders that made it', 'Ordrar som nådde fram')}
            icon={Flag}
            tone={{
              wash: 'from-emerald-400/16',
              icon: 'bg-emerald-100 text-emerald-800',
            }}
          />
          <PulseCard
            label={t('Organizations', 'Organisationer')}
            value={pulse?.organizations ?? 0}
            hint={t('Companies on the platform', 'Företag på plattformen')}
            icon={Building2}
            tone={{
              wash: 'from-violet-400/16',
              icon: 'bg-violet-100 text-violet-700',
            }}
          />
          <PulseCard
            label={t('Emails waiting', 'Mejl som väntar')}
            value={pulse?.emailsWaiting ?? 0}
            hint={t('Inbox drafts to review', 'Utkast att granska')}
            icon={Mail}
            tone={{
              wash: 'from-orange-400/16',
              icon: 'bg-orange-100 text-orange-700',
            }}
          />
          <PulseCard
            label={t('Strategy patches', 'Strategiförslag')}
            value={pulse?.pendingSuggestions ?? 0}
            hint={t('HITL, not auto-apply', 'HITL, inte auto')}
            icon={Sparkles}
            tone={{
              wash: 'from-amber-400/16',
              icon: 'bg-amber-100 text-amber-800',
            }}
          />
          <PulseCard
            label={t('Open todos', 'Öppna todos')}
            value={pulse?.openTodos ?? 0}
            hint={t('On the roadmap below', 'På roadmappen nedan')}
            icon={Check}
            tone={{
              wash: 'from-teal-400/16',
              icon: 'bg-teal-100 text-teal-800',
            }}
          />
        </div>
      </Column>

      {historyDoc ? (
        <Column className="gap-4">
          <Column className="gap-0.5">
            <Row className="items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">
                {t('How we got here', 'Hur vi kom hit')}
              </p>
            </Row>
            <p className="text-sm text-muted-foreground">
              {t(
                'Curated origin story. Edit in Slack with @Brain show history. The daily learn job does not rewrite this.',
                'Kuraterad ursprungshistoria. Ändra i Slack med @Brain show history. Det dagliga lärjobbet skriver inte om den.'
              )}
            </p>
          </Column>
          <Column className="prose prose-sm max-w-none rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-background p-6 shadow-soft prose-headings:font-medium prose-p:text-muted-foreground">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {historyDoc.title}
            </p>
            <ReactMarkdown>{historyDoc.body}</ReactMarkdown>
          </Column>
        </Column>
      ) : null}

      <Column className="gap-4">
        <Column className="gap-0.5">
          <p className="text-sm font-medium">
            {t('Roadmap with dates', 'Roadmap med datum')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              'Milestones, dates, and the todos that make them real.',
              'Milstolpar, datum och todosen som gör dem verkliga.'
            )}
          </p>
        </Column>
        <Column className="gap-5">
          {(milestones ?? []).map((milestone) => (
            <MilestoneCard
              key={milestone.id}
              milestone={milestone}
              locale={dateLocale}
            />
          ))}
        </Column>
        <form
          className="flex flex-wrap items-end gap-2 pl-8"
          onSubmit={(event) => {
            event.preventDefault()
            if (!newTitle.trim() || !newDate) return
            addMilestone.mutate(
              { title: newTitle.trim(), targetDate: newDate },
              {
                onSuccess: () => {
                  setNewTitle('')
                  setNewDate('')
                },
              }
            )
          }}
        >
          <Input
            value={newTitle}
            onChange={(event) => setNewTitle(event.target.value)}
            placeholder={t('New milestone', 'Ny milstolpe')}
            className="h-9 max-w-xs"
          />
          <Input
            type="date"
            value={newDate}
            onChange={(event) => setNewDate(event.target.value)}
            className="h-9 w-40"
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={!newTitle.trim() || !newDate || addMilestone.isPending}
          >
            {t('Add milestone', 'Lägg till milstolpe')}
          </Button>
        </form>
      </Column>

      <Column className="gap-4">
        <Column className="gap-0.5">
          <p className="text-sm font-medium">
            {t('What we believe', 'Vad vi tror på')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              'Living strategy from the company brain. Edit in Slack with @Brain.',
              'Levande strategi från bolagshjärnan. Ändra i Slack med @Brain.'
            )}
          </p>
        </Column>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {orderedDocs.map((doc) => (
            <StrategyCard
              key={doc.id}
              doc={doc}
              selected={selected?.id === doc.id}
              onSelect={() => setSelectedSlug(doc.slug)}
            />
          ))}
        </div>
        {selected ? (
          <Column className="prose prose-sm max-w-none rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-background p-6 shadow-soft prose-headings:font-medium prose-p:text-muted-foreground">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {selected.slug.replace(/_/g, ' ')}
            </p>
            <ReactMarkdown>{selected.body}</ReactMarkdown>
          </Column>
        ) : null}
      </Column>
    </PageContainer>
  )
}
